import { For, Show, createMemo, createSignal, onCleanup } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import wm from '../lib/wmStore.js';
import { gold, qty, relTime, weekRangeLabel } from '../lib/format.js';
import { pickGuild } from '../lib/picks.js';
import TradeWeekPicker from '../components/TradeWeekPicker.jsx';
import Tooltip from '../components/MouseTooltip.jsx';
import { tipProps } from '../components/MouseTooltip.jsx';
import { cleanFissalOutput } from '../lib/fissalCleanup.js';
import { renderFissalMarkdown } from '../lib/renderMarkdown.jsx';
import {
  fissalHttpFallback,
  fissalNetworkFallback,
  fissalEmptyFallback,
  isEmptyFissalReply,
} from '../lib/fissalFallback.js';

const VIEWS = [
  { key: 'guilds', label: 'By Guild' },
  { key: 'zones', label: 'By Zone / NPC' },
];

function fmtBid(value) {
  const n = Number(value) || 0;
  return n > 0 ? gold(Math.round(n)) : '—';
}

function fmtRangeTop(range) {
  const estimate = Number(range?.estimate) || 0;
  if (estimate > 0) return gold(Math.round(estimate));
  const low = Number(range?.low) || 0;
  const high = Number(range?.high) || 0;
  if (low > 0 && high > 0) return `${gold(low)}–${gold(high)}`;
  return '—';
}

function fmtRangeBottom(range) {
  const low = Number(range?.low) || 0;
  const high = Number(range?.high) || 0;
  if (low > 0 && high > 0) return `${gold(low)}–${gold(high)}`;
  return range?.source || '—';
}

function guildBadgeText(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'GW';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatHubTier(tier) {
  if (!tier) return '';
  switch (tier) {
    case 'capital_hub': return 'Capital';
    case 'chapter_hub': return 'Chapter';
    case 'major_city': return 'City';
    case 'minor_town': return 'Town';
    case 'starter_island': return 'Starter';
    case 'rural_outpost': return 'Rural';
    case 'outlaw_refuge': return 'Outlaw';
    default: return tier.replace(/_/g, ' ');
  }
}

function fmtRange(range) {
  if (!range) return '—';
  const low = Number(range.low) || 0;
  const high = Number(range.high) || 0;
  const est = Number(range.estimate) || 0;
  if (est > 0 && low > 0 && high > 0) return `${gold(est)} (${gold(low)}–${gold(high)})`;
  if (low > 0 && high > 0) return `${gold(low)}–${gold(high)}`;
  if (est > 0) return gold(est);
  return '—';
}

function fmtWeek(ts) {
  if (!ts) return '—';
  const d = new Date(Number(ts));
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function locationLabel(loc) {
  if (!loc) return '—';
  return loc.city && loc.city !== loc.zone ? `${loc.city} · ${loc.zone}` : (loc.zone || loc.city || '—');
}

function isSameLocation(a, b) {
  if (!a || !b) return false;
  return a.zone === b.zone && a.city === b.city && a.trader === b.trader;
}

function confidenceLabel(range) {
  const c = Number(range?.confidence || 0);
  return c > 0 ? `${Math.round(c * 100)}% confidence` : range?.source || 'model estimate';
}

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function buildTooltipHtml({ icon = '📊', kicker, title, summary, rows = [], sections = [], insight, tone = 'warm' }) {
  const rowHtml = rows
    .filter(row => row && row.value != null && row.value !== '')
    .map(row => `
      <div class="v2-kiosks__tip-row">
        <span class="v2-kiosks__tip-row-icon">${escapeHtml(row.icon || '•')}</span>
        <span class="v2-kiosks__tip-row-label">${escapeHtml(row.label || '')}</span>
        <strong class="v2-kiosks__tip-row-value">${escapeHtml(row.value)}</strong>
      </div>`)
    .join('');
  const sectionHtml = sections
    .filter(section => section && (section.items || []).length)
    .map(section => `
      <div class="v2-kiosks__tip-section">
        <div class="v2-kiosks__tip-section-title">${escapeHtml(section.label || '')}</div>
        <ul class="v2-kiosks__tip-list">
          ${(section.items || []).map(item => `<li>${escapeHtml(item)}</li>`).join('')}
        </ul>
      </div>`)
    .join('');
  return `
    <div class="v2-kiosks__tooltip is-${escapeHtml(tone)}">
      <div class="v2-kiosks__tip-head">
        <span class="v2-kiosks__tip-icon">${escapeHtml(icon)}</span>
        <div class="v2-kiosks__tip-head-copy">
          ${kicker ? `<div class="v2-kiosks__tip-kicker">${escapeHtml(kicker)}</div>` : ''}
          <div class="v2-kiosks__tip-title">${escapeHtml(title || '')}</div>
        </div>
      </div>
      ${summary ? `<div class="v2-kiosks__tip-summary">${escapeHtml(summary)}</div>` : ''}
      ${rowHtml ? `<div class="v2-kiosks__tip-rows">${rowHtml}</div>` : ''}
      ${sectionHtml}
      ${insight ? `<div class="v2-kiosks__tip-insight">${escapeHtml(insight)}</div>` : ''}
    </div>`;
}

function fmtDateTime(ts) {
  if (!ts) return 'Unknown fetch time';
  return new Date(Number(ts)).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function weekOffsetLabel(weeksAgo) {
  const n = Math.max(0, Number(weeksAgo) || 0);
  return n === 0 ? 'Current trade week' : `${n} week${n === 1 ? '' : 's'} ago`;
}

function rangeTooltip(range, label = 'Bid range') {
  if (!range) {
    return buildTooltipHtml({
      icon: '📉',
      kicker: 'Bid range',
      title: label,
      summary: 'No modeled range available yet.',
      insight: 'This kiosk needs more observed bid context before the estimate becomes useful.',
    });
  }
  const estimate = Number(range.estimate) || 0;
  const low = Number(range.low) || 0;
  const high = Number(range.high) || 0;
  const confidence = Number(range.confidence || 0);
  const spread = low > 0 && high > 0 ? Math.max(0, high - low) : 0;
  const spreadPct = estimate > 0 && spread > 0 ? Math.round((spread / estimate) * 100) : 0;
  let insight = 'Estimate-only view, so staff should treat the number as directional rather than hard truth.';
  if (spread > 0 && estimate > 0) {
    insight = spreadPct <= 18
      ? 'Read: tight lane, bidding pressure looks fairly established.'
      : spreadPct <= 40
        ? 'Read: moderate swing, expect a real contest but with a usable baseline.'
        : 'Read: wide swing, treat this as directional and plan for volatility.';
  }
  const rows = [
    { icon: '🏆', label: 'Estimate', value: estimate > 0 ? gold(Math.round(estimate)) : '—' },
    { icon: '📏', label: 'Span', value: low > 0 && high > 0 ? `${gold(low)}–${gold(high)}` : '—' },
    { icon: '↔️', label: 'Width', value: spread > 0 ? `${gold(spread)}${spreadPct > 0 ? ` (${spreadPct}%)` : ''}` : '—' },
    { icon: '🎯', label: 'Confidence', value: confidence > 0 ? `${Math.round(confidence * 100)}%` : '—' },
    { icon: '🧠', label: 'Source', value: range.source || 'model estimate' },
  ];

  if (range.kioskStats) {
    const ks = range.kioskStats;
    if (ks.hubTier) {
      rows.push({ icon: '🏛️', label: 'Hub Tier', value: formatHubTier(ks.hubTier) });
    }
    if (ks.historicalLossFloor) {
      rows.push({ icon: '🛡️', label: 'Loss Floor', value: gold(ks.historicalLossFloor) });
    }
    if (ks.historicalWinAvg) {
      rows.push({ icon: '📈', label: 'Win Avg', value: gold(ks.historicalWinAvg) });
    }
    if (ks.churnRate != null) {
      rows.push({ icon: '🔄', label: 'Churn Rate', value: `${Math.round(ks.churnRate * 100)}%` });
    }
    if (ks.dominantGuild) {
      rows.push({ icon: '👑', label: 'Dominant Guild', value: ks.dominantGuild });
    }
  }

  return buildTooltipHtml({
    icon: '📈',
    kicker: 'Bid range',
    title: label,
    summary: 'Modeled winning band for this kiosk snapshot.',
    rows,
    insight,
  });
}

function weeksHeldTooltip(weeksHeld) {
  const n = Math.max(0, Number(weeksHeld) || 0);
  if (!n) {
    return buildTooltipHtml({
      icon: '🕰️',
      kicker: 'Hold length',
      title: 'Weeks held',
      summary: 'No hold-length data available.',
      insight: 'Without hold history, displacement risk needs to be inferred from the bid range alone.',
      tone: 'cool',
    });
  }
  return buildTooltipHtml({
    icon: n >= 16 ? '🏰' : n >= 4 ? '🛡️' : '🎒',
    kicker: 'Hold length',
    title: `${n} consecutive weeks`,
    summary: n >= 16
      ? 'Entrenched kiosk holder with a long defense record.'
      : n >= 4
        ? 'Stable kiosk holder across multiple trade cycles.'
        : 'Fresh hold where movement risk is still relatively high.',
    rows: [
      { icon: '📆', label: 'Weeks held', value: `${n} weeks` },
      { icon: '⚠️', label: 'Risk posture', value: n >= 16 ? 'Low movement risk' : n >= 4 ? 'Moderate movement risk' : 'Higher movement risk' },
    ],
    insight: n >= 16
      ? 'Expect challengers to need a serious overbid to dislodge this guild.'
      : n >= 4
        ? 'This kiosk has inertia, but a strong challenger can still force movement.'
        : 'Recent captures flip more often, so watch the next bid cycle closely.',
    tone: 'cool',
  });
}

function weekRangeTooltip(label, weeksAgo) {
  return buildTooltipHtml({
    icon: '🗓️',
    kicker: 'Widget scope',
    title: label || 'Latest sourced week',
    summary: 'The entire widget is filtered to this trade-week snapshot.',
    rows: [
      { icon: '⌛', label: 'Snapshot', value: weekOffsetLabel(weeksAgo) },
      { icon: '🔎', label: 'Use for', value: 'Comparing owners and bid pressure across weeks' },
    ],
    insight: 'Adjust the week picker to spot displacement patterns and repeat defenders.',
    tone: 'cool',
  });
}

function fetchedTooltip(ts) {
  return buildTooltipHtml({
    icon: '📡',
    kicker: 'Sheet freshness',
    title: fmtDateTime(ts),
    summary: 'Latest ingest time for the kiosk sheet behind this snapshot.',
    rows: [
      { icon: '🕒', label: 'Fetched', value: fmtDateTime(ts) },
      { icon: '🧭', label: 'Use for', value: 'Judging whether a real-world kiosk may already have moved' },
    ],
    insight: 'If this is old, treat all hold and bid-read conclusions as increasingly stale.',
    tone: 'cool',
  });
}

function zoneWeekTooltip(zoneName, weeksAgo) {
  return buildTooltipHtml({
    icon: '🧭',
    kicker: 'Zone owners',
    title: `${zoneName || 'Zone'} snapshot`,
    summary: 'Zone trader ownership is being shown for this selected week.',
    rows: [
      { icon: '⌛', label: 'Week', value: weekOffsetLabel(weeksAgo) },
      { icon: '🔁', label: 'Use for', value: 'Checking prior owners without leaving zone mode' },
    ],
    insight: 'This is the fastest way to compare how sticky a zone is across resets.',
    tone: 'cool',
  });
}

function trackedTradersTooltip(zone) {
  return buildTooltipHtml({
    icon: '🧾',
    kicker: 'Zone coverage',
    title: zone?.zone || 'Zone',
    summary: 'Tracked trader count in this zone slice.',
    rows: [
      { icon: '🏪', label: 'Traders', value: `${qty(zone?.holderCount || 0)} tracked` },
      { icon: '🏙️', label: 'City', value: zone?.city || 'Unknown city' },
      { icon: '📦', label: 'Tier', value: zone?.tier ? `Tier ${zone.tier}` : 'Unknown tier' },
    ],
    insight: 'Higher trader counts usually mean more owner churn to inspect when pressure rises.',
    tone: 'cool',
  });
}

function empiricalStatsTooltip(zone) {
  const stats = zone?.empiricalStats || {};
  return buildTooltipHtml({
    icon: '🏛️',
    kicker: 'Empirical History (5-Year Tri-Guild)',
    title: `${zone?.zone || 'Zone'} Bid History`,
    summary: 'Actual confirmed wins and outbid floors from 5,774 bids across Post, Caravan, and Dealers.',
    rows: [
      { icon: '🏆', label: 'Confirmed wins', value: stats.redfurWinCount ? `${stats.redfurWinCount} wins` : '0 wins' },
      { icon: '📊', label: 'Total bids tracked', value: stats.totalTrackedBids ? `${stats.totalTrackedBids} bids` : '—' },
      { icon: '💰', label: 'Average win', value: stats.historicalWinAvg ? gold(stats.historicalWinAvg) : '—' },
      { icon: '📈', label: 'Highest recorded win', value: stats.historicalWinMax ? gold(stats.historicalWinMax) : '—' },
      { icon: '🛑', label: 'Outbid loss floor', value: stats.historicalLossFloor ? gold(stats.historicalLossFloor) : '—' },
    ],
    insight: stats.historicalLossFloor
      ? `Any bid below ${gold(stats.historicalLossFloor)} has historically been outbid in competitive cycles.`
      : 'Calibrated using empirical bid records and Tamriel United tradecraft.',
    tone: 'warm',
  });
}

function sourceConfidenceTooltip(range) {
  return buildTooltipHtml({
    icon: '🧠',
    kicker: 'Model confidence',
    title: confidenceLabel(range),
    summary: 'How reliable the current bid model looks for this kiosk.',
    rows: [
      { icon: '🎯', label: 'Confidence', value: confidenceLabel(range) },
      { icon: '🧪', label: 'Range source', value: range?.source || 'model estimate' },
    ],
    insight: 'Use this alongside the span width. Wide ranges with weak confidence need human judgment.',
  });
}

function statusTooltip(status) {
  if (status === 'usual') {
    return buildTooltipHtml({
      icon: '🛡️',
      kicker: 'Location status',
      title: 'Usual spot',
      summary: 'This guild is sitting in its standard historical kiosk position.',
      insight: 'Stable placement usually means staff should focus more on bid pressure than on movement.',
      tone: 'cool',
    });
  }
  if (status === 'displaced') {
    return buildTooltipHtml({
      icon: '⚠️',
      kicker: 'Location status',
      title: 'Displaced',
      summary: 'This guild moved away from its normal kiosk lane.',
      insight: 'Displacement often signals zone pressure, a failed defense, or a strategic relocation.',
    });
  }
  return buildTooltipHtml({
    icon: '✨',
    kicker: 'Location status',
    title: 'New baseline',
    summary: 'No historical baseline exists yet for this guild or slot.',
    insight: 'Treat this as a new pattern forming rather than a break from prior behavior.',
    tone: 'cool',
  });
}

function sourceConflictTooltip(listingCount) {
  return buildTooltipHtml({
    icon: '⚔️',
    kicker: 'Listing conflict',
    title: `${listingCount} listings found`,
    summary: 'ESO permits one kiosk per guild, so multiple listings indicate source disagreement.',
    rows: [
      { icon: '📚', label: 'Conflicts', value: `${listingCount} overlapping entries` },
      { icon: '🧹', label: 'Action', value: 'Inspect individual holdings before trusting the headline spot' },
    ],
    insight: 'These guild cards need manual review before they are used for high-confidence bidding calls.',
  });
}

function amenityTooltip(kind, detail) {
  return buildTooltipHtml({
    icon: kind === 'Bank' ? '🏦' : '🪨',
    kicker: 'Zone amenity',
    title: kind,
    summary: kind === 'Bank' ? 'A bank is close to these traders.' : 'Wayshrine access is close enough to matter.',
    rows: detail ? [{ icon: '📍', label: 'Detail', value: detail }] : [],
    insight: 'Amenities can increase convenience-driven traffic and make otherwise average kiosks punch above their tier.',
    tone: 'cool',
  });
}

function locationTooltip(title, location, sub, status) {
  return buildTooltipHtml({
    icon: status === 'displaced' ? '🧳' : status === 'usual' ? '📍' : '✨',
    kicker: 'Location detail',
    title,
    summary: 'Kiosk placement context for this guild.',
    rows: [
      { icon: '🗺️', label: 'Area', value: location || '—' },
      { icon: '🧑', label: 'Trader', value: sub || 'NPC unknown' },
      { icon: '🏷️', label: 'Status', value: status || 'Unclassified' },
    ],
    insight: status === 'displaced'
      ? 'Movement away from the usual spot is often more informative than the raw bid estimate.'
      : 'Use location stability to decide whether this guild behaves predictably week to week.',
    tone: 'cool',
  });
}

function holdingsTooltip(row) {
  return buildTooltipHtml({
    icon: '🗃️',
    kicker: 'Guild footprint',
    title: 'Current holdings',
    summary: 'How many source rows currently point at this guild plus the summed modeled power.',
    rows: [
      { icon: '🏪', label: 'Holdings', value: qty(row?.holdingsCount || 0) },
      { icon: '💰', label: 'Total modeled power', value: fmtBid(row?.totalEstimate) },
      { icon: '🧩', label: 'Listing count', value: qty(row?.listingCount || 0) },
    ],
    insight: 'More than one current row usually means the data needs reconciliation before you act on it.',
    tone: 'cool',
  });
}

function historyTooltip(guildName, hist) {
  return buildTooltipHtml({
    icon: '🕰️',
    kicker: 'Historical location',
    title: guildName,
    summary: 'Prior recorded kiosk position for this guild.',
    rows: [
      { icon: '📆', label: 'Week', value: fmtWeek(hist?.weekStartTs) },
      { icon: '🗺️', label: 'Spot', value: locationLabel(hist) },
      { icon: '🧑', label: 'Trader', value: hist?.trader || '—' },
      { icon: '💸', label: 'Bid', value: fmtRange(hist?.bidRange) },
    ],
    insight: 'Clicking this row pivots the guild picker straight to that historical trade week.',
    tone: 'cool',
  });
}

function traderHistoryTooltip(trader) {
  const ks = trader?.bidRange?.kioskStats;
  const hub = trader?.hubTier || ks?.hubTier;
  const rows = [
    { icon: '🏁', label: 'Current owner', value: trader?.guildName || '—' },
    { icon: '💸', label: 'Current bid', value: fmtRange(trader?.bidRange) },
  ];
  if (hub) {
    rows.push({ icon: '🏛️', label: 'Hub tier', value: formatHubTier(hub) });
  }
  if (ks?.historicalLossFloor) {
    rows.push({ icon: '🛡️', label: 'Loss floor', value: gold(ks.historicalLossFloor) });
  }
  if (ks?.historicalWinAvg) {
    rows.push({ icon: '📈', label: 'Win average', value: gold(ks.historicalWinAvg) });
  }
  if (ks?.churnRate != null) {
    rows.push({ icon: '🔄', label: 'Churn rate', value: `${Math.round(ks.churnRate * 100)}%` });
  }
  if (ks?.dominantGuild) {
    rows.push({ icon: '👑', label: 'Dominant guild', value: ks.dominantGuild });
  }

  return buildTooltipHtml({
    icon: trader?.isOutlaw ? '🕳️' : '🧭',
    kicker: 'Trader history',
    title: trader?.trader || 'Unnamed NPC',
    summary: trader?.isOutlaw ? 'Outlaw Refuge trader with historical owner context.' : 'Recent owner history for this NPC trader.',
    rows,
    sections: [{
      label: 'Recent owners',
      items: (trader?.history || []).slice(0, 8).map(h => `${fmtWeek(h.weekStartTs)} · ${h.guildName || '—'} · ${fmtRange(h.bidRange)}`),
    }],
    insight: 'Use the owner trail to spot sticky traders and recurring guild preferences.',
    tone: 'cool',
  });
}

function buildPrompt(question, context) {
  return [
    'You are Fissal acting as a kiosk bid-pattern analyst for Redfur staff.',
    'Use the kiosk context below to produce practical bid guidance. Keep blind-bid uncertainty explicit.',
    'Focus on guild movement, zone pressure, range risk, and what staff should inspect next.',
    '',
    'KIOSK CONTEXT:',
    JSON.stringify(context || {}, null, 2),
    '',
    'STAFF QUESTION:',
    question,
  ].join('\n');
}

export default function Kiosks(props) {
  const uid = () => String(props.widget?.id || 'kiosks').replace(/[^A-Za-z0-9_-]/g, '-');
  const guildListId = () => `v2-kiosks-guilds-${uid()}`;
  const zoneListId = () => `v2-kiosks-zones-${uid()}`;
  const traderListId = () => `v2-kiosks-traders-${uid()}`;
  const [view, setView] = createSignal(['guilds', 'zones'].includes(props.settings?.view) ? props.settings.view : 'guilds');
  const [search, setSearch] = createSignal(props.settings?.search || '');
  const [guildName, setGuildName] = createSignal(props.settings?.guildName || '');
  const [zone, setZone] = createSignal(props.settings?.zone || '');
  const [trader, setTrader] = createSignal(props.settings?.trader || '');
  const [debouncedFilters, setDebouncedFilters] = createSignal({
    search: props.settings?.search || '',
    guildName: props.settings?.guildName || '',
    zone: props.settings?.zone || '',
    trader: props.settings?.trader || '',
  });
  const [weekOffset, setWeekOffset] = createSignal(Number(props.settings?.weekOffset || 0));
  const [expandedGuild, setExpandedGuild] = createSignal(null);
  const [expandedZone, setExpandedZone] = createSignal(null);
  const [zoneWeeks, setZoneWeeks] = createSignal({});
  const [isAssistantOpen, setIsAssistantOpen] = createSignal(props.settings?.isAssistantOpen !== false);

  function persistKioskSettings(patch) {
    if (!props.widget?.id) return;
    try {
      wm.setSettings(props.widget.id, { ...(props.widget.settings || {}), ...patch });
    } catch { /* best-effort */ }
  }

  let filterTimer = null;
  function scheduleFilterCommit() {
    if (filterTimer) clearTimeout(filterTimer);
    filterTimer = setTimeout(() => {
      filterTimer = null;
      const next = {
        search: search(),
        guildName: guildName(),
        zone: zone(),
        trader: trader(),
      };
      setDebouncedFilters(next);
      persistKioskSettings({
        search: next.search || null,
        guildName: next.guildName || null,
        zone: next.zone || null,
        trader: next.trader || null,
      });
    }, 220);
  }

  const { data, loading, error, refetch } = useWidgetData('kiosks', () => {
    const df = debouncedFilters();
    return {
      view: view(),
      search: df.search,
      guildName: df.guildName,
      zone: df.zone,
      trader: view() === 'zones' ? df.trader : '',
      weekOffset: weekOffset(),
      limit: props.settings?.limit || 150,
    };
  });
  const sourceNotes = createMemo(() => data()?.sourceNotes || []);
  const distincts = createMemo(() => data()?.distincts || { guilds: [], zones: [], cities: [], traders: [] });
  const weekRange = createMemo(() => weekRangeLabel(data()?.weekStartTs));

  const hasActiveFilters = () => !!(search() || guildName() || zone() || trader() || weekOffset() !== 0);

  function clearFilters() {
    if (filterTimer) { clearTimeout(filterTimer); filterTimer = null; }
    setSearch('');
    setGuildName('');
    setZone('');
    setTrader('');
    setDebouncedFilters({ search: '', guildName: '', zone: '', trader: '' });
    setWeekOffset(0);
    setZoneWeeks({});
    persistKioskSettings({ search: null, guildName: null, zone: null, trader: null, weekOffset: 0 });
  }

  function setZoneWeek(zoneName, next) {
    setZoneWeeks(prev => ({ ...prev, [zoneName]: Math.max(0, Math.min(59, Number(next) || 0)) }));
  }

  function changeTopWeek(next) {
    const v = Number(next.weekOffset);
    if (Number.isFinite(v) && v >= 0) {
      const clamped = Math.min(59, v);
      setWeekOffset(clamped);
      persistKioskSettings({ weekOffset: clamped });
    }
  }

  function updateView(nextView) {
    setView(nextView);
    persistKioskSettings({ view: nextView });
  }

  function toggleAssistant() {
    const next = !isAssistantOpen();
    setIsAssistantOpen(next);
    persistKioskSettings({ isAssistantOpen: next });
  }

  return (
    <div class="v2-widget v2-kiosks v2-kiosks--staged">
      <div class="v2-kiosks__toolbar">
        <div class="v2-kiosks__mode" role="tablist" aria-label="Kiosk organization">
          <For each={VIEWS}>{item => (
            <button
              type="button"
              class={`v2-gt-chip${view() === item.key ? ' is-on' : ''}`}
              onClick={() => updateView(item.key)}
            >{item.label}</button>
          )}</For>
          <button
            type="button"
            class={`v2-gt-chip v2-kiosks__analyst-toggle${isAssistantOpen() ? ' is-on' : ''}`}
            style="margin-left: 6px; border-color: var(--fissal);"
            onClick={toggleAssistant}
          >
            {isAssistantOpen() ? '🤖 Hide Analyst' : '🤖 Bid Analyst'}
          </button>
        </div>
        <div class="v2-kiosks__week">
          <span class="v2-kiosks__week-label">{weekOffset() === 0 ? 'Current trade week' : `${weekOffset()} weeks ago`}</span>
          <TradeWeekPicker
            value={{ weekOffset: weekOffset() }}
            onChange={changeTopWeek}
            maxWeeksAgo={59}
            allowAllTime={false}
            allowCustom={false}
            inlineNumeric={true}
            nativeTitles={false}
          />
          <Show when={hasActiveFilters()}>
            <button
              class="v2-btn-sm v2-kiosks__clear-btn"
              onClick={clearFilters}
              style="padding: 2px 6px;"
              {...tipProps({ content: 'Reset all filters and week offset', pin: false })}
            >
              ✕ Clear
            </button>
          </Show>
          <button class="v2-widget-hdr__btn" onClick={refetch} {...tipProps({ content: 'Refresh kiosk data', pin: false })}>↻</button>
        </div>
      </div>

      <div class="v2-kiosks__meta">
        <DataChip
          label="Week"
          value={weekRange() || 'Latest sourced week'}
          tipHtml={weekRangeTooltip(weekRange(), weekOffset())}
        />
        <Show when={data()?.lastFetchedAt}>
          <DataChip
            label="Sheet sync"
            value={relTime(data().lastFetchedAt)}
            tipHtml={fetchedTooltip(data().lastFetchedAt)}
            tone="muted"
          />
        </Show>
      </div>

      <div class="v2-kiosks__filters">
        <input class={`v2-kiosk__filter v2-kiosks__search${view() === 'zones' ? ' is-zones-view' : ''}`} placeholder="Search guilds, zones, NPCs…" value={search()} onInput={e => { setSearch(e.currentTarget.value); scheduleFilterCommit(); }} />
        <input class="v2-kiosk__filter" list={guildListId()} placeholder="Guild" value={guildName()} onInput={e => { setGuildName(e.currentTarget.value); scheduleFilterCommit(); }} />
        <input class="v2-kiosk__filter" list={zoneListId()} placeholder="Zone or city" value={zone()} onInput={e => { setZone(e.currentTarget.value); scheduleFilterCommit(); }} />
        <Show when={view() === 'zones'}>
          <input class="v2-kiosk__filter" list={traderListId()} placeholder="NPC trader" value={trader()} onInput={e => { setTrader(e.currentTarget.value); scheduleFilterCommit(); }} />
        </Show>
        <datalist id={guildListId()}><For each={distincts().guilds}>{v => <option value={v} />}</For></datalist>
        <datalist id={zoneListId()}><For each={[...(distincts().zones || []), ...(distincts().cities || [])]}>{v => <option value={v} />}</For></datalist>
        <datalist id={traderListId()}><For each={distincts().traders}>{v => <option value={v} />}</For></datalist>
      </div>

      <Show when={!loading()} fallback={<div class="v2-widget-skel">Loading kiosks…</div>}>
        <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>
          <div class={`v2-kiosks__layout${isAssistantOpen() ? ' has-assistant' : ' is-collapsed'}`}>
            <div class="v2-kiosks__main v2-widget__scroll">
              <Show when={view() === 'guilds'}>
                <GuildsView rows={data()?.guilds || []} expanded={expandedGuild()} onToggle={setExpandedGuild} weekOffset={weekOffset()} />
              </Show>
              <Show when={view() === 'zones'}>
                <ZonesView
                  rows={data()?.zones || []}
                  expanded={expandedZone()}
                  onToggle={setExpandedZone}
                  zoneWeeks={zoneWeeks()}
                  onZoneWeek={setZoneWeek}
                />
              </Show>
              <Show when={sourceNotes().length}>
                <div class="v2-kiosks__notes"><For each={sourceNotes()}>{note => <span>{note}</span>}</For></div>
              </Show>
            </div>
            <Show when={isAssistantOpen()}>
              <BidAssistant context={data()?.assistantContext} view={view()} onClose={() => setIsAssistantOpen(false)} />
            </Show>
          </div>
        </Show>
      </Show>
    </div>
  );
}

function GuildsView(props) {
  return (
    <div class="v2-kiosks__list v2-kiosks__list--guilds">
      <Show when={props.rows.length} fallback={<div class="v2-empty">No guild holdings match these filters.</div>}>
        <For each={props.rows}>{row => {
          const isSameLoc = () => isSameLocation(row.currentLocation, row.usualLocation);
          const hasConflictingListings = () => (Number(row.listingCount) || 0) > 1;
          const guildLogoUrl = () => row.guildLogoUrl || row.logoUrl || row.iconUrl || row.emblemUrl || null;
          return (
            <section class={`v2-kiosk-card${props.expanded === row.guildName ? ' is-open' : ''}`}>
              <button type="button" class="v2-kiosk-card__summary" onClick={() => props.onToggle(props.expanded === row.guildName ? null : row.guildName)}>
                <span class="v2-kiosk-card__chev">{props.expanded === row.guildName ? '▾' : '▸'}</span>
                <span class="v2-kiosk-card__titleblock">
                  <span class="v2-kiosk-card__titleblock-row">
                    <span class="v2-kiosk-card__guild-mark" aria-hidden="true">
                      <Show
                        when={guildLogoUrl()}
                        fallback={<span class="v2-kiosk-card__guild-fallback">{guildBadgeText(row.guildName)}</span>}
                      >
                        <img class="v2-kiosk-card__guild-logo" src={guildLogoUrl()} alt="" loading="lazy" />
                      </Show>
                    </span>
                    <span class="v2-kiosk-card__title v2-pick v2-pick--guild" onClick={(e) => { e.stopPropagation(); pickGuild(row.guildName, { weekOffset: props.weekOffset }); }}>
                      {row.guildName}
                    </span>
                    <Show when={hasConflictingListings()}>
                      <span class="v2-kiosks__holdings-badge" {...tipProps({ html: sourceConflictTooltip(row.listingCount), pin: false })}>
                        {row.listingCount} listings
                      </span>
                    </Show>
                  </span>
                  <span class="v2-kiosk-card__sub">{locationLabel(row.currentLocation)} · {row.currentLocation?.trader || 'NPC unknown'}</span>
                </span>
                <span class="v2-kiosk-card__metric v2-kiosk-card__metric--guild">
                  <span class="v2-kiosk-card__metric-top">
                    <Show when={row.currentLocation?.weeksHeld} fallback={
                      <DataChip
                        compact={true}
                        label="Model"
                        value={confidenceLabel(row.currentBidRange)}
                        tipHtml={sourceConfidenceTooltip(row.currentBidRange)}
                        tone="muted"
                      />
                    }>
                      <span
                        class={`v2-kiosks__tenure-badge ${row.currentLocation.weeksHeld >= 16 ? 'is-entrenched' : row.currentLocation.weeksHeld >= 4 ? 'is-stable' : ''}`}
                        {...tipProps({ html: weeksHeldTooltip(row.currentLocation.weeksHeld), pin: false })}
                      >
                        {row.currentLocation.weeksHeld >= 16 ? '🏰 ' : row.currentLocation.weeksHeld >= 4 ? '🛡️ ' : ''}
                        {row.currentLocation.weeksHeld}w held
                      </span>
                    </Show>
                    <span class="v2-kiosk-card__metric-emoji" aria-hidden="true">📈</span>
                    <b>{fmtRangeTop(row.currentBidRange)}</b>
                  </span>
                  <small class="v2-kiosk-card__metric-bottom">
                    <DataChip
                      compact={true}
                      label="Range"
                      value={fmtRangeBottom(row.currentBidRange)}
                      tipHtml={rangeTooltip(row.currentBidRange, `${row.guildName} current range`)}
                    />
                  </small>
                </span>
              </button>
              <Show when={props.expanded === row.guildName}>
                <div class="v2-kiosk-card__body">
                  <div class="v2-kiosks__detail-grid">
                    <InfoTile
                      label="Current spot"
                      value={locationLabel(row.currentLocation)}
                      sub={row.currentLocation?.trader || 'No NPC name sourced'}
                      status={isSameLoc() ? 'usual' : row.usualLocation ? 'displaced' : 'new'}
                      tipHtml={locationTooltip('Current spot', locationLabel(row.currentLocation), row.currentLocation?.trader || 'No NPC name sourced', isSameLoc() ? 'usual' : row.usualLocation ? 'displaced' : 'new')}
                    />
                    <InfoTile
                      label="Usual spot"
                      value={locationLabel(row.usualLocation)}
                      sub={row.usualLocation ? (row.usualLocation.trader || 'NPC name unknown') : 'No history found'}
                      status={row.usualLocation ? 'usual' : null}
                      tipHtml={locationTooltip('Usual spot', locationLabel(row.usualLocation), row.usualLocation ? (row.usualLocation.trader || 'NPC name unknown') : 'No history found', row.usualLocation ? 'usual' : 'new')}
                    />
                    <InfoTile
                      label="Average bid power"
                      value={fmtRange(row.usualLocation?.averageBidRange || row.currentBidRange)}
                      sub={confidenceLabel(row.usualLocation?.averageBidRange || row.currentBidRange)}
                      tipHtml={rangeTooltip(row.usualLocation?.averageBidRange || row.currentBidRange, `${row.guildName} average bid power`)}
                    />
                    <InfoTile
                      label="Current holdings"
                      value={qty(row.holdingsCount)}
                      sub={`Total modeled power ${fmtBid(row.totalEstimate)}`}
                      tipHtml={holdingsTooltip(row)}
                    />
                  </div>
                  <Show when={hasConflictingListings()}>
                    <div class="v2-kiosks__mini-section">
                      <div class="v2-kiosks__mini-title">Conflicting current listings</div>
                      <For each={row.holdings}>{holding => (
                        <div class="v2-kiosks__holding-row">
                          <DataChip compact={true} wrap={true} label="Spot" value={locationLabel(holding)} tipHtml={locationTooltip('Conflicting spot', locationLabel(holding), holding.trader || '—', 'conflict')} tone="muted" />
                          <DataChip compact={true} wrap={true} label="Trader" value={holding.trader || '—'} tipHtml={locationTooltip('Trader detail', locationLabel(holding), holding.trader || '—', 'conflict')} tone="muted" />
                          <DataChip compact={true} label="Bid" value={fmtRange(holding.bidRange)} tipHtml={rangeTooltip(holding.bidRange, `${row.guildName} conflicting bid`)} />
                        </div>
                      )}</For>
                    </div>
                  </Show>
                  <div class="v2-kiosks__mini-section">
                    <div class="v2-kiosks__mini-title">Historical locations</div>
                    <For each={row.history || []}>{hist => (
                      <button type="button" class="v2-kiosks__history-row v2-kiosks__history-row--button" onClick={() => pickGuild(row.guildName, { weekOffset: hist.weekOffset })} {...tipProps({ html: historyTooltip(row.guildName, hist), pin: false })}>
                        <DataChip compact={true} label="Week" value={fmtWeek(hist.weekStartTs)} tone="muted" focusable={false} />
                        <span>{locationLabel(hist)} · {hist.trader || '—'}</span>
                        <DataChip compact={true} label="Bid" value={fmtRange(hist.bidRange)} focusable={false} />
                      </button>
                    )}</For>
                    <Show when={!row.history?.length}><div class="v2-empty">No history available for this guild.</div></Show>
                  </div>
                </div>
              </Show>
            </section>
          );
        }}</For>
      </Show>
    </div>
  );
}

function ZonesView(props) {
  return (
    <div class="v2-kiosks__list v2-kiosks__list--zones">
      <Show when={props.rows.length} fallback={<div class="v2-empty">No zones match these filters.</div>}>
        <For each={props.rows}>{zone => {
          const zoneWeek = () => Number(props.zoneWeeks[zone.zone] ?? 0);
          const visibleTrader = (trader) => {
            const wk = zoneWeek();
            if (wk === 0) return trader.history?.find(h => h.weekOffset === 0) || trader;
            return trader.history?.find(h => h.weekOffset === wk) || {
              ...trader,
              guildName: null,
              weeksHeld: null,
              weekOffset: wk,
              isVacant: true,
            };
          };
          return (
            <section class={`v2-kiosk-card v2-kiosk-card--zone${props.expanded === zone.zone ? ' is-open' : ''}`}>
              <button type="button" class="v2-kiosk-card__summary" onClick={() => props.onToggle(props.expanded === zone.zone ? null : zone.zone)}>
                <span class="v2-kiosk-card__chev">{props.expanded === zone.zone ? '▾' : '▸'}</span>
                <span class="v2-kiosk-card__titleblock">
                  <span class="v2-kiosk-card__title">{zone.zone}</span>
                  <span class="v2-kiosk-card__sub">
                    {zone.city || 'Unknown city'} · Tier {zone.tier || '?'}{zone.isDLC ? ' (DLC)' : ''}
                    <Show when={zone.hasBank}> · <span class="v2-kiosks__inline-badge" {...tipProps({ html: amenityTooltip('Bank'), pin: false })}>Bank</span></Show>
                    <Show when={zone.hasWayshrine}> · <span class="v2-kiosks__inline-badge" {...tipProps({ html: amenityTooltip('Wayshrine', `Distance ${zone.wayshrineDistance || 1}`), pin: false })}>Wayshrine</span></Show>
                  </span>
                </span>
                <span class="v2-kiosk-card__metric">
                  <b>{fmtRange(zone.bidRange)}</b>
                  <small style="display: flex; gap: 4px; justify-content: flex-end; align-items: center;">
                    <Show when={zone.empiricalStats?.redfurWinCount > 0}>
                      <DataChip
                        compact={true}
                        label="Redfur"
                        value={`${zone.empiricalStats.redfurWinCount}w`}
                        tipHtml={empiricalStatsTooltip(zone)}
                        tone="green"
                      />
                    </Show>
                    <DataChip
                      compact={true}
                      label="Traders"
                      value={`${qty(zone.holderCount)} tracked`}
                      tipHtml={trackedTradersTooltip(zone)}
                      tone="muted"
                    />
                  </small>
                </span>
              </button>
              <Show when={props.expanded === zone.zone}>
                <div class="v2-kiosk-card__body">
                  <div class="v2-kiosks__zone-intel">
                    <For each={zone.analytics || []}>{bit => {
                      const isHighVis = () => bit.includes('high visibility') || bit.includes('high pre');
                      const isBank = () => bit.includes('bank');
                      const isDlc = () => bit.includes('DLC');
                      const isWin = () => bit.includes('win') || bit.includes('Redfur');
                      const isFloor = () => bit.includes('floor');
                      return (
                        <span class={`v2-kiosks__intel-pill${isHighVis() ? ' is-highvis' : isBank() ? ' is-bank' : isDlc() ? ' is-dlc' : isWin() ? ' is-win' : isFloor() ? ' is-floor' : ''}`}>
                          {bit}
                        </span>
                      );
                    }}</For>
                  </div>
                  <div class="v2-kiosks__zone-week">
                    <DataChip
                      compact={true}
                      label="Owners"
                      value={weekOffsetLabel(zoneWeek())}
                      tipHtml={zoneWeekTooltip(zone.zone, zoneWeek())}
                    />
                    <TradeWeekPicker
                      value={{ weekOffset: zoneWeek() }}
                      onChange={(next) => props.onZoneWeek(zone.zone, next.weekOffset)}
                      maxWeeksAgo={59}
                      allowAllTime={false}
                      allowCustom={false}
                      inlineNumeric={true}
                      nativeTitles={false}
                    />
                  </div>
                  <TraderSection title="Zone traders" rows={zone.traders || []} visibleTrader={visibleTrader} />
                </div>
              </Show>
            </section>
          );
        }}</For>
      </Show>
    </div>
  );
}

function TraderSection(props) {
  return (
    <Show when={props.rows.length}>
      <div class="v2-kiosks__mini-section">
        <div class="v2-kiosks__mini-title">{props.title}</div>
        <For each={props.rows}>{trader => {
          const shown = () => props.visibleTrader(trader);
          return (
            <div class="v2-kiosks__trader-row">
              <Tooltip html={traderHistoryTooltip(trader)} pin={false} class="v2-kiosks__trader-tip">
                <span class="v2-kiosks__trader-name">
                  {trader.trader || 'Unnamed NPC'}
                  <Show when={trader.isOutlaw}>
                    <span class="v2-kiosks__outlaw-badge">Outlaw</span>
                  </Show>
                  <Show when={trader.hubTier && !trader.isOutlaw}>
                    <span class={`v2-kiosks__hub-badge is-${trader.hubTier}`}>
                      {formatHubTier(trader.hubTier)}
                    </span>
                  </Show>
                </span>
              </Tooltip>
              <Show when={shown().guildName} fallback={<span class="v2-kiosks__trader-guild-none">—</span>}>
                <span
                  class="v2-pick v2-pick--guild"
                  onClick={() => pickGuild(shown().guildName, { weekOffset: shown().weekOffset || 0 })}
                >
                  {shown().guildName}
                </span>
              </Show>
              <DataChip compact={true} label="Bid" value={fmtRange(shown().bidRange)} tipHtml={rangeTooltip(shown().bidRange, `${shown().guildName || trader.trader || 'Trader'} bid`)} />
              <small style="text-align: right;">
                <Show when={shown().weeksHeld} fallback={<DataChip compact={true} label="Model" value={confidenceLabel(shown().bidRange)} tipHtml={sourceConfidenceTooltip(shown().bidRange)} tone="muted" />}>
                  <span class="v2-kiosks__tenure-badge-mini" {...tipProps({ html: weeksHeldTooltip(shown().weeksHeld), pin: false })}>
                    {shown().weeksHeld}w
                  </span>
                </Show>
              </small>
            </div>
          );
        }}</For>
      </div>
    </Show>
  );
}

function InfoTile(props) {
  return (
    <div class={`v2-kiosks__info-tile${props.status ? ` is-${props.status}` : ''}`}>
      <span class="v2-kiosks__info-tile-label">
        <span>{props.label}</span>
        <Show when={props.status === 'usual'}>
          <span class="v2-kiosks__badge v2-kiosks__badge--green" {...tipProps({ html: statusTooltip('usual'), pin: false })}>Usual</span>
        </Show>
        <Show when={props.status === 'displaced'}>
          <span class="v2-kiosks__badge v2-kiosks__badge--orange" {...tipProps({ html: statusTooltip('displaced'), pin: false })}>Displaced</span>
        </Show>
        <Show when={props.status === 'new'}>
          <span class="v2-kiosks__badge v2-kiosks__badge--blue" {...tipProps({ html: statusTooltip('new'), pin: false })}>New</span>
        </Show>
      </span>
      <b>{props.value || '—'}</b>
      <Show when={props.sub}>
        <DataChip compact={true} wrap={true} tone="muted" value={props.sub || ''} tipHtml={props.tipHtml} />
      </Show>
    </div>
  );
}

function DataChip(props) {
  return (
    <span
      class={`v2-kiosks__chip${props.compact ? ' is-compact' : ''}${props.tone ? ` is-${props.tone}` : ''}${props.wrap ? ' can-wrap' : ''}`}
      tabIndex={props.focusable === false ? undefined : '0'}
      {...tipProps({ html: props.tipHtml, content: props.tip, pin: false })}
    >
      <Show when={props.label}><span class="v2-kiosks__chip-label">{props.label}</span></Show>
      <span class="v2-kiosks__chip-value">{props.value}</span>
    </span>
  );
}

function BidAssistant(props) {
  const [question, setQuestion] = createSignal('Which guilds look likely to overpay or move next week?');
  const [answer, setAnswer] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const context = () => ({ view: props.view, ...(props.context || {}) });

  const PRESETS = [
    'Which guilds look likely to overpay or move next week?',
    'Identify high pressure zones with bid risk.',
    'Are there any notable displacement candidates?'
  ];

  async function ask(questionOverride) {
    const q = String(questionOverride ?? question()).trim();
    if (!q || busy()) return;
    setBusy(true);
    setAnswer('');
    try {
      const r = await fetch('/api/fissal/analyze', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: buildPrompt(q, context()), contextType: 'general', weeksAgo: Number(props.context?.weekOffset || 0) }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setAnswer(fissalHttpFallback(r.status, j));
        return;
      }
      const cleaned = cleanFissalOutput(j.analysis || j.response || j.text || j.reply || '');
      setAnswer(isEmptyFissalReply(cleaned) ? fissalEmptyFallback() : cleaned);
    } catch {
      setAnswer(fissalNetworkFallback());
    } finally {
      setBusy(false);
    }
  }

  return (
    <aside class="v2-kiosks__assistant">
      <div class="v2-kiosks__assistant-hdr">
        <span>Fissal Bid Analyst</span>
        <div style="display: flex; gap: 6px; align-items: center;">
          <button class="v2-btn-sm" onClick={ask} disabled={busy()}>{busy() ? 'Thinking…' : 'Analyze'}</button>
          <Show when={props.onClose}>
            <button class="v2-widget-hdr__btn v2-kiosks__panel-close" style="padding: 1px 4px;" onClick={props.onClose} {...tipProps({ content: 'Close analyst panel', pin: false })}>✕</button>
          </Show>
        </div>
      </div>
      <textarea
        class="v2-kiosks__assistant-input"
        value={question()}
        onInput={e => setQuestion(e.currentTarget.value)}
        onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) ask(); }}
      />
      <div class="v2-kiosks__assistant-presets">
        <For each={PRESETS}>{preset => (
          <button
            type="button"
            class="v2-kiosks__preset-chip"
            onClick={() => { setQuestion(preset); ask(preset); }}
            disabled={busy()}
          >
            {preset}
          </button>
        )}</For>
      </div>
      <Show when={answer()} fallback={<div class="v2-kiosks__assistant-empty">Uses sourced kiosk history, zone facts, observed bid data, and available sales signals. Presets auto-trigger analysis.</div>}>
        <div class="v2-kiosks__assistant-answer-box">
          <div class="v2-kiosks__assistant-answer">{renderFissalMarkdown(answer())}</div>
        </div>
      </Show>
    </aside>
  );
}
